/**
 * Hamsa tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const hamsaTattoosPack: ToilePack = {
    slug: 'hamsa-tattoos',
    category: 'tattoo',
    name: 'Hamsa tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'eye', label: 'Hamsa and eye', subject: 'a hamsa hand with fingers pointing up, an open eye drawn in the centre of the palm'},
        {key: 'mandala', label: 'Mandala hamsa', subject: "a hamsa hand filled with mandala patterns, petals and dot work, a lotus at the palm's centre"},
        {key: 'beads', label: 'Hamsa and beads', subject: 'a hamsa hand with fingers pointing down, a short string of beads and a small crescent hanging below the wrist'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'blackwork', label: 'Blackwork', style: 'a blackwork tattoo drawing, solid black fills against bare white paper, strong contrast and no gray', palette: 'black ink only', framing: 'centered with a wide white margin'},
    ],
    directions: ['a lotus', 'gold ink', 'wrist'],
    directionHint: 'try: a lotus',
    seo: {
        title: 'Hamsa tattoos: hand with eye, mandala, beads',
        metaDescription: 'Nine hamsa tattoo designs, the protective hand: an eye in its palm, mandala patterns or hanging beads, in fine line, dotwork and blackwork.',
        h1: 'Hamsa tattoo designs: an eye, mandala patterns, hanging beads',
        tagline: 'Three hamsas, each drawn in fine line, dotwork and blackwork on white paper.',
        intro: 'The hamsa, the hand of Fatima or of Miriam, is a protective sign across the Middle East and North Africa. These nine show a hamsa with an open eye in its palm, a hamsa filled with mandala patterns, and a hamsa with beads hanging below. Each is drawn as fine line, dotwork or blackwork. Type a different sign or a twist, such as a crescent moon above the fingers, and the three styles carry the request.',
        useCases: [
            'a wrist or forearm design to take to an artist',
            'a protective gift for a friend or family member',
            'a print for a hallway wall',
            'a phone wallpaper',
        ],
        faq: [
            {q: 'What is in the nine designs?', a: 'A hamsa with an eye in its palm, a hamsa filled with mandala patterns, and a hamsa with beads hanging below. Each one is drawn in three styles.'},
            {q: 'Can I print these?', a: 'Each design sits on a plain white background in dark line work, so it reads clearly on paper.'},
            {q: 'What is the difference between the three styles?', a: 'Fine line is one hairline stroke with almost no shading. Dotwork builds its shading from dots. Blackwork uses solid black fills against bare white paper.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type the subject or twist you want, such as a crescent moon above the fingers. The nine shown are samples, and a request is not a promised result.'},
        ],
    },
}
